<template>
  <div>
    <div class="mb-1">
      <h4 class="mb-0">
        Заглушка (техработы)
      </h4>
      <div class="text-muted small">
        Страницу техработ показывает edge-router перед сайтом — по ручному флагу ниже или по проверкам сайта и API, если
        включено автоматическое включение. Админка и API заглушкой не закрываются. Изменения подхватываются за один
        интервал проверок. Администраторы могут открыть сайт под заглушкой — кнопкой ниже.
      </div>
    </div>

    <b-alert
      v-if="errorText"
      show
      variant="danger"
      class="mb-1"
    >
      {{ errorText }}
    </b-alert>

    <b-card
      no-body
      class="mb-1 switch-card"
      :class="switchClass"
    >
      <b-card-body class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="mb-50">
          <div class="d-flex align-items-center mb-50">
            <b-badge
              :variant="manual ? 'danger' : 'light-secondary'"
              class="mr-1"
            >
              {{ manual ? 'Включена вручную' : 'Ручной флаг выключен' }}
            </b-badge>
            <span class="text-muted">
              Автоматически: <strong>{{ autoText }}</strong>
            </span>
          </div>
          <div
            v-if="progressText"
            class="switch-progress d-flex align-items-center"
          >
            <b-spinner
              small
              class="mr-50"
            />
            {{ progressText }}
          </div>
          <div
            v-else
            class="small text-muted"
          >
            Ручной флаг держит заглушку, что бы ни показывали проверки. Снимите его, когда работы закончены.
          </div>
        </div>

        <div class="d-flex flex-wrap">
          <b-button
            v-if="!manual"
            variant="danger"
            size="lg"
            :disabled="!overview || !!saving"
            @click="$refs.onModal.show()"
          >
            <b-spinner
              v-if="saving === 'off'"
              small
              class="mr-50"
            />
            Включить заглушку
          </b-button>
          <b-button
            v-else
            variant="outline-success"
            size="lg"
            :disabled="!!saving"
            @click="$refs.offModal.show()"
          >
            <b-spinner
              v-if="saving === 'on'"
              small
              class="mr-50"
            />
            Снять заглушку
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <b-card
      title="Состояние на серверах"
      class="mb-1"
    >
      <div
        v-if="!overview"
        class="text-center py-1"
      >
        <b-spinner small />
      </div>
      <div
        v-else
        class="behind d-flex flex-wrap align-items-center justify-content-between mb-1 p-1"
        :class="state.visible.length ? 'behind-on' : state.pending ? 'behind-pending' : ''"
      >
        <div class="mr-1 mb-50">
          <maintenance-status-text :state="state" />
          <div
            v-if="state.visible.length"
            class="small text-muted"
          >
            Администратор может открыть сайт под ней: ссылка действует минуту, вход — 2 часа в этом браузере.
          </div>
        </div>
        <b-button
          v-if="state.visible.length && state.bypassConfigured"
          variant="danger"
          size="lg"
          :disabled="opening"
          @click="openBehind"
        >
          <feather-icon icon="LogInIcon" /> Войти на сайт как администратор
        </b-button>
        <div
          v-else-if="state.visible.length"
          class="small text-danger"
        >
          Вход под заглушку не настроен на сервере: нет MAINTENANCE_BYPASS_SECRET
        </div>
      </div>
      <b-alert
        v-if="overview && !overview.guards.length"
        show
        variant="warning"
        class="mb-0"
      >
        edge-router ещё не прислал отчёт с момента запуска API. Либо он не стоит перед сайтом на этом окружении (на проде
        пока так — заглушка из админки там не работает), либо у него неверный WATCHDOG_KILL_SWITCH_TOKEN (отчёт не
        принимается, но заглушка всё равно работает).
      </b-alert>
      <div
        v-for="guard in overview ? overview.guards : []"
        :key="guard.guard"
        class="guard-row py-1"
      >
        <div class="d-flex flex-wrap align-items-center mb-50">
          <b-badge
            :variant="guard.active ? 'danger' : 'success'"
            class="mr-1"
          >
            {{ guard.active ? 'Заглушка показана' : 'Сайт открыт' }}
          </b-badge>
          <strong class="mr-1">{{ guard.destination }}</strong>
          <span class="text-muted small mr-1">{{ guard.guard }} · {{ guard.version }}</span>
          <b-badge
            v-if="guard.lost"
            variant="warning"
            class="mr-50"
          >
            нет отчётов с {{ formatTime(guard.receivedAt) }}
          </b-badge>
          <b-badge
            v-if="guard.configStale"
            variant="light-warning"
          >
            работает на сохранённых настройках
          </b-badge>
        </div>
        <div
          v-if="guard.reason"
          class="mb-50"
        >
          Причина: {{ guard.reason }}
        </div>
        <div class="d-flex flex-wrap small">
          <div
            v-for="check in checksOf(guard)"
            :key="check.label"
            class="mr-2 mb-25"
          >
            <feather-icon
              :icon="check.ok ? 'CheckCircleIcon' : 'XCircleIcon'"
              :class="check.ok ? 'text-success' : 'text-danger'"
            />
            {{ check.label }}:
            <template v-if="check.ok">
              отвечает
            </template>
            <template v-else>
              {{ check.error || 'не отвечает' }}
            </template>
            <span class="text-muted">({{ check.streak }} подряд)</span>
          </div>
        </div>
        <div class="small text-muted">
          Отчёт: {{ formatTime(guard.receivedAt) }}
        </div>
      </div>
    </b-card>

    <b-card
      title="Управление и автоматика"
      class="mb-1"
    >
      <settings-category-form
        ref="controlForm"
        category="maintenance"
        @changed="onControlChanged"
      />
    </b-card>

    <b-row>
      <b-col xl="6">
        <b-card
          title="Вид заглушки"
          class="mb-1"
        >
          <settings-category-form
            category="maintenance_page"
            @changed="load"
          />
        </b-card>
      </b-col>
      <b-col xl="6">
        <b-card
          no-body
          class="mb-1"
        >
          <b-card-header class="d-flex justify-content-between align-items-center">
            <b-card-title class="mb-0">
              Предпросмотр
            </b-card-title>
            <b-button
              variant="flat-secondary"
              size="sm"
              :href="previewUrl"
              target="_blank"
            >
              <feather-icon icon="ExternalLinkIcon" /> Открыть
            </b-button>
          </b-card-header>
          <iframe
            v-if="overview"
            :key="previewUrl"
            :src="previewUrl"
            title="Предпросмотр заглушки"
            class="preview"
          />
        </b-card>
      </b-col>
    </b-row>

    <b-modal
      ref="onModal"
      title="Включить заглушку?"
      ok-title="Включить"
      ok-variant="danger"
      cancel-title="Отмена"
      :ok-disabled="!!saving"
      @ok="evt => setManual(evt, true)"
    >
      <p class="mb-0">
        Все посетители сайта увидят страницу техработ
        в течение одного интервала проверок ({{ overview ? overview.config.intervalSeconds : '…' }} с). Админка и API
        продолжат работать.
      </p>
    </b-modal>
    <b-modal
      ref="offModal"
      title="Снять заглушку?"
      ok-title="Снять"
      ok-variant="success"
      cancel-title="Отмена"
      :ok-disabled="!!saving"
      @ok="evt => setManual(evt, false)"
    >
      <p class="mb-0">
        Сайт снова откроется, если проверки проходят. Если включено автоматическое включение и сайт или API сейчас
        недоступны, заглушка останется до их восстановления.
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BCardBody, BCardHeader, BCardTitle, BCol, BModal, BRow, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SettingsCategoryForm from '@/views/settings/SettingsCategoryForm.vue'
import {
  describeMaintenance, maintenanceState, openBehindMaintenance, refreshMaintenance, setManualMaintenance, startApplying,
  subscribeMaintenance,
} from './maintenance-state'
import MaintenanceStatusText from './MaintenanceStatusText.vue'

export default {
  name: 'MaintenancePage',
  components: {
    BAlert, BBadge, BButton, BCard, BCardBody, BCardHeader, BCardTitle, BCol, BModal, BRow, BSpinner, MaintenanceStatusText, SettingsCategoryForm,
  },
  directives: { 'b-tooltip': VBTooltip },
  data() {
    return {
      opening: false,
      unsubscribe: null,
    }
  },
  computed: {
    /** Shared with the banner above every page, so both follow a save at once */
    overview() {
      return maintenanceState.overview
    },
    errorText() {
      return maintenanceState.error
    },
    state() {
      return describeMaintenance(this.overview)
    },
    manual() {
      return !!this.overview?.config.manual
    },
    /** 'on' | 'off' while the manual flag is being saved */
    saving() {
      return maintenanceState.saving
    },
    /** From the click until the edge routers apply it: the card shows where the switch is */
    progressText() {
      if (this.saving) return this.saving === 'on' ? 'Сохраняем: включаем заглушку…' : 'Сохраняем: снимаем заглушку…'
      const { applying } = maintenanceState
      if (!applying) return ''
      const interval = this.overview?.config.intervalSeconds
      const wait = interval ? ` (до ~${interval} с)` : ''
      return applying.target === 'on'
        ? `Сохранено. Ждём, пока edge-router покажет заглушку посетителям${wait}…`
        : `Сохранено. Ждём, пока edge-router уберёт заглушку${wait}…`
    },
    switchClass() {
      if (this.saving || maintenanceState.applying) return 'switch-card-pending'
      return this.manual ? 'switch-card-on' : ''
    },
    autoText() {
      const c = this.overview?.config
      if (!c) return '…'
      if (!c.auto) return 'выключено'
      const watched = [c.watchSite && 'сайт', c.watchApi && 'API'].filter(Boolean)
      return watched.length ? `при недоступности: ${watched.join(', ')}` : 'включено, но ни одна проверка не выбрана'
    },
    /** The admin's copy of config/error-pages/503.html reads the texts from #cfg= */
    previewUrl() {
      const page = this.overview?.config.page || {}
      return `${process.env.BASE_URL}maintenance-preview.html#cfg=${encodeURIComponent(JSON.stringify(page))}`
    },
  },
  created() {
    this.unsubscribe = subscribeMaintenance()
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {
    load() {
      return refreshMaintenance()
    },
    /** The modal closes at once: the card above shows the progress, the banner toasts when visitors see it */
    async setManual(evt, value) {
      evt.preventDefault()
      const modal = value ? this.$refs.onModal : this.$refs.offModal
      modal.hide()
      try {
        await setManualMaintenance(value)
        this.toast('Сохранено', value ? 'Заглушка включается — ждём edge-router' : 'Ручной флаг снят — ждём edge-router', 'success')
        await this.$refs.controlForm.fetch()
      } catch (e) {
        this.toast('Ошибка', e.message, 'danger')
      }
    },
    /** The switch «Заглушка включена вручную» in the form is the same flag as the big button */
    onControlChanged(setting) {
      if (setting && setting.key === 'maintenance.manual') return startApplying(setting.value)
      return this.load()
    },
    async openBehind() {
      this.opening = true
      try {
        await openBehindMaintenance()
      } catch (e) {
        this.toast('Ошибка', e.message, 'danger')
      } finally {
        this.opening = false
      }
    },
    checksOf(guard) {
      return [
        guard.site && { label: 'Сайт', ...guard.site },
        guard.api && { label: 'API', ...guard.api },
      ].filter(Boolean)
    },
    formatTime(value) {
      return value ? new Date(value).toLocaleString('ru-RU') : '—'
    },
    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: variant === 'success' ? 'BellIcon' : 'AlertTriangleIcon',
        },
      })
    },
  },
}
</script>

<style scoped>
.switch-card {
  border-left: 4px solid transparent;
  transition: border-color 0.2s, background-color 0.2s;
}
.switch-card-on {
  border-left-color: #ea5455;
}
.switch-card-pending {
  border-left-color: #ff9f43;
  background: rgba(255, 159, 67, 0.06);
}
.switch-progress {
  font-weight: 500;
  color: #ff9f43;
}
.behind {
  border: 1px solid rgba(40, 199, 111, 0.4);
  border-radius: 0.428rem;
  background: rgba(40, 199, 111, 0.06);
}
.behind-on {
  border-color: rgba(234, 84, 85, 0.4);
  background: rgba(234, 84, 85, 0.06);
}
.behind-pending {
  border-color: rgba(255, 159, 67, 0.5);
  background: rgba(255, 159, 67, 0.08);
}
.guard-row + .guard-row {
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}
.preview {
  width: 100%;
  height: 640px;
  border: 0;
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}
</style>
